import { useEffect, useState } from 'react';

/**
 * Picks the preview-plate tone for best contrast behind an uploaded logo by
 * sampling the artwork's average luminance (alpha-weighted, so a transparent
 * logo is judged on its ink, not its empty space): a light/white mark sits on the
 * dark plate, a dark/black mark on the light one, so the logo never disappears
 * into its backing.
 *
 * Same model as the top-bar brand switcher (`BrandSelector` `useLogoFrame`) and
 * the "Website logos" preview in Casino Settings (`LogosSection` `useAutoFrame`),
 * duplicated here rather than shared because the customizer keeps its logo helpers
 * page-local (see `lib/logos.ts`).
 *
 * Falls back to the dark plate while there is no source, before measuring, or when
 * the pixels can't be read (a cross-origin CDN asset whose canvas is tainted) so
 * it degrades safely.
 */
export const useLogoFrame = (src: string | null): 'light' | 'dark' => {
  const [frame, setFrame] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    setFrame('dark');
    if (!src) return undefined;

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = (): void => {
      // Unreachable / 404 / network error — keep the dark fallback.
      if (!cancelled) setFrame('dark');
    };
    img.onload = (): void => {
      if (cancelled) return;
      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let lum = 0;
        let alpha = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          lum += (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) * a;
          alpha += a;
        }
        if (alpha === 0) return;
        setFrame(lum / alpha > 140 ? 'dark' : 'light');
      } catch {
        // Tainted canvas (no CORS) — keep the dark fallback.
      }
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  return frame;
};
